import { useEffect, useMemo, useState } from "react";
import {
  MiAlert,
  MiButton,
  MiModalConfirm,
  MiPlaceholderArea,
  MiPlaceholderIcon,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  ENTITY_NAMES,
  MODAL_TITLES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  IStepTemplateResponse,
  IVerticalTabProps,
  IWorkOrderTemplateDetailsResponse,
  IWorkOrderTemplateListItemResponse,
  PlatformModules,
} from "@miplatform/types";
import { mdiFormatListBulleted, mdiPlus } from "@mdi/js";
import { VerticalTabsLayout } from "@mibuildweb/components";
import {
  usePermissions,
  useStepTemplates,
  useWorkOrderTemplates,
} from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { ConfigWorkOrderStepCard } from "./components/ConfigWorkOrderStepCard";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigAccordionHeader } from "../components/ConfigAccordionHeader/ConfigAccordionHeader";

export function ConfigWorkOrderStepsList({
  module,
}: Readonly<{ module: PlatformModules }>) {
  const [search, setSearch] = useState("");
  const [allWorkOrderTemplates, setAllWorkOrderTemplates] = useState<
    (IWorkOrderTemplateListItemResponse & {
      stepTemplates: IStepTemplateResponse[];
    })[]
  >([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedStep, setSelectedStep] =
    useState<IStepTemplateResponse | null>(null);

  const theme = useTheme();

  const {
    isLoading: isStepTemplateLoading,
    refresh,
    getStepTemplatesByModule,
    deleteStepTemplate,
  } = useStepTemplates({
    assignmentTemplateId: selectedStep?.assignmentTemplateId,
  });
  const {
    isLoading: isWorkTemplateLoading,
    getWorkOrderTemplates,
    setFilter,
    filter,
  } = useWorkOrderTemplates({
    module,
  });

  const fetchWorkOrderTemplates = async () => {
    const workOrderTemplatesResponse = await getWorkOrderTemplates();
    const stepTemplatesResponse = await getStepTemplatesByModule(module);
    if (workOrderTemplatesResponse.data && stepTemplatesResponse.data) {
      const wotWithSteps = workOrderTemplatesResponse.data.map(
        (wot: IWorkOrderTemplateDetailsResponse) => ({
          ...wot,
          stepTemplates: stepTemplatesResponse.data.filter(
            (step: IStepTemplateResponse) =>
              (wot.assignmentTemplates || []).some(
                (at) => at.id === step.assignmentTemplateId
              )
          ),
        })
      );
      setAllWorkOrderTemplates(wotWithSteps);
    }
  };

  useEffect(() => {
    fetchWorkOrderTemplates();
  }, [module, refresh]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IWorkOrderTemplateListItemResponse> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const handleOpenDeleteModal = (step: IStepTemplateResponse) => {
    setSelectedStep(step);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedStep) {
      deleteStepTemplate(selectedStep.id, selectedStep);
      setSelectedStep(null);
      setOpenDeleteModal(false);
    }
  };

  const tabs = useMemo(() => {
    const tabs = [
      {
        label: "All Steps",
        value: "All Steps",
        filter: {},
      },
    ];
    allWorkOrderTemplates.forEach((template) => {
      tabs.push({
        label: template.name,
        value: template.id,
        filter: { name: template.name },
      });
    });
    return tabs;
  }, [allWorkOrderTemplates]);

  return (
    <ConfigPageLayout
      title="Work Order Steps"
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      loading={isWorkTemplateLoading || isStepTemplateLoading}
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={tabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
        transparentBackground
      >
        {allWorkOrderTemplates.length > 0 && (
          <MiAlert
            description="Select an existing step or click “add step” to add a new step to the selected work order."
            storageKey="configWorkOrderStepsListAlert"
            sx={{ marginBottom: 2 }}
          />
        )}
        {allWorkOrderTemplates.length === 0 && (
          <MiPlaceholderArea
            bgColor={theme.palette.common.white}
            iconComponent={
              <MiPlaceholderIcon
                icon={mdiFormatListBulleted}
                text={`No ${ENTITY_NAMES.WORK_ORDER_STEPS} found`}
              />
            }
          />
        )}
        {allWorkOrderTemplates
          .filter(
            (wot) =>
              (!filter.name || filter.name === wot.name) &&
              (search === "" ||
                wot.name.toLowerCase().includes(search.toLowerCase()))
          )
          .map((wot) => {
            const steps = wot.stepTemplates || [];
            return (
              <StepsAccordion
                key={wot.name}
                wot={wot}
                steps={steps}
                handleOpenDeleteModal={handleOpenDeleteModal}
              />
            );
          })}
      </VerticalTabsLayout>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedStep?.title}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
}

const StepsAccordion = ({
  wot,
  steps,
  handleOpenDeleteModal,
}: StepsAccordionProps) => {
  const { navigate } = useRouter();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const canCreate = hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.CREATE);

  return (
    <Box sx={{ marginBottom: 2 }}>
      <ConfigAccordionHeader
        title={`${wot.name} Steps`}
        customAction={
          canCreate && (
            <MiButton
              title={`Add ${wot.name} Step`}
              variant="contained"
              size="small"
              startIcon={{ path: mdiPlus }}
              sx={{ marginRight: theme.spacing(1) }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`add-work-order-step/${wot.id}`);
              }}
            />
          )
        }
      >
        {steps.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {steps.map((step: IStepTemplateResponse) => (
              <ConfigWorkOrderStepCard
                key={step.id}
                step={step}
                workOrderTemplate={wot}
                handleOpenDeleteModal={handleOpenDeleteModal}
              />
            ))}
          </Box>
        ) : (
          <MiPlaceholderArea
            text="No records to display"
            bgColor={theme.palette.primary.light}
            containerStyle={{
              padding: theme.spacing(4),
            }}
          />
        )}
      </ConfigAccordionHeader>
    </Box>
  );
};

interface StepsAccordionProps {
  wot: IWorkOrderTemplateListItemResponse & {
    stepTemplates: IStepTemplateResponse[];
  };
  steps: IStepTemplateResponse[];
  handleOpenDeleteModal: (step: IStepTemplateResponse) => void;
}
